import { z } from 'zod';
import {
  paginatedResponse,
  paginationQuery,
  nonNegativeDecimalString as decimalString,
} from './common';

/**
 * Schemas for `DrawerSystem` (parent product family, e.g. "Blum Tandem Box")
 * and its `DrawerSystemVariant` rows (concrete buyable SKUs, e.g. "500mm soft-close 30kg").
 *
 * The split mirrors the legacy `Sertare Kreart.xlsx` workbook (one section per
 * system, rows per length/load-rating/feature combo) and powers the drawer
 * picker in the UI: pick a system, then pick a variant. `priceNet` lives on
 * the variant and is used by the drawer calculator.
 */

export const drawerSystemCurrencyEnum = z.enum(['RON', 'EUR', 'USD']);
export type DrawerSystemCurrencyValue = z.infer<typeof drawerSystemCurrencyEnum>;

export const drawerSystemVariantAttributes = z
  .object({
    sourceSheet: z.string().optional(),
    sourceRow: z.number().int().optional(),
    sourceKind: z.enum(['BOX', 'GLISARE', 'UNKNOWN']).optional(),
    systemHeader: z.string().optional(),
    productLine: z.string().optional(),
    productName: z.string().optional(),
    tipSertar: z.string().nullable().optional(),
    descriere: z.string().nullable().optional(),
    detaliu: z.string().nullable().optional(),
    hBox: z.string().nullable().optional(),
    hBoxHeightMm: z.number().int().nullable().optional(),
    depthMm: z.number().int().nullable().optional(),
    color: z.string().nullable().optional(),
    interior: z.boolean().optional(),
    supplier: z.string().nullable().optional(),
    supplierCode: z.string().nullable().optional(),
    url: z.string().nullable().optional(),
  })
  .passthrough();
export type DrawerSystemVariantAttributes = z.infer<typeof drawerSystemVariantAttributes>;

const optionalTrim = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .optional()
    .transform((v) => (v === '' ? undefined : v));

/**
 * A multi-select filter value. Accepts a repeated query param
 * (`?hBox=M&hBox=K`) or a comma-separated one (`?hBox=M,K`) — no catalog value
 * contains a comma. Absent or empty means "no constraint on this field", which
 * is what makes every drawer facet optional.
 */
const optionalList = (max: number) =>
  z
    .union([z.string(), z.array(z.string())])
    .optional()
    .transform((v) => {
      if (v === undefined) return undefined;
      const parts = (Array.isArray(v) ? v : [v])
        .flatMap((entry) => entry.split(','))
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0 && entry.length <= max);
      return parts.length > 0 ? parts : undefined;
    });

const optionalIntList = () =>
  z
    .union([z.string(), z.array(z.string()), z.number(), z.array(z.number())])
    .optional()
    .transform((v) => {
      if (v === undefined) return undefined;
      const raw: Array<string | number> = Array.isArray(v) ? v : [v];
      const parts: number[] = [];
      for (const entry of raw) {
        const tokens: Array<string | number> =
          typeof entry === 'number' ? [entry] : entry.split(',');
        for (const token of tokens) {
          const parsed = typeof token === 'number' ? token : Number(token.trim());
          if (Number.isInteger(parsed) && parsed > 0) parts.push(parsed);
        }
      }
      return parts.length > 0 ? parts : undefined;
    });

// ---------- DrawerSystemVariant ----------

export const drawerSystemVariantListItem = z.object({
  id: z.string(),
  systemId: z.string(),
  systemName: z.string().optional(),
  description: z.string(),
  offerName: z.string().nullable(),
  sku: z.string().nullable(),
  internalCode: z.string().nullable(),
  priceNet: decimalString.optional(),
  currency: drawerSystemCurrencyEnum,
  /**
   * Default price multiplier for this bought-in slide system, used every time
   * the variant is picked.
   *
   * A DEFAULT, not the truth: each use snapshots it onto
   * `Drawer.systemCoefficient`, so changing it here never re-prices work
   * already quoted. It lives on the variant, next to the `priceNet` it marks
   * up, rather than on the parent system — the same reason the price does.
   */
  coefficient: decimalString,
  active: z.boolean(),
  attributes: drawerSystemVariantAttributes.nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type DrawerSystemVariantListItem = z.infer<typeof drawerSystemVariantListItem>;

export const drawerSystemVariantDetails = drawerSystemVariantListItem.extend({
  attributes: drawerSystemVariantAttributes.nullable(),
});
export type DrawerSystemVariantDetails = z.infer<typeof drawerSystemVariantDetails>;

export const createDrawerSystemVariantRequest = z.object({
  description: z.string().trim().min(1).max(200),
  offerName: optionalTrim(200),
  sku: optionalTrim(80),
  internalCode: optionalTrim(120),
  priceNet: decimalString,
  currency: drawerSystemCurrencyEnum.default('RON'),
  coefficient: decimalString.default('2.5'),
  active: z.boolean().default(true),
  attributes: z.unknown().optional(),
});
export type CreateDrawerSystemVariantRequest = z.infer<typeof createDrawerSystemVariantRequest>;

export const updateDrawerSystemVariantRequest = createDrawerSystemVariantRequest.partial();
export type UpdateDrawerSystemVariantRequest = z.infer<typeof updateDrawerSystemVariantRequest>;

// ---------- DrawerSystem ----------

export const drawerSystemListItem = z.object({
  id: z.string(),
  name: z.string(),
  manufacturer: z.string().nullable(),
  active: z.boolean(),
  variantCount: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type DrawerSystemListItem = z.infer<typeof drawerSystemListItem>;

export const drawerSystemDetails = drawerSystemListItem.omit({ variantCount: true }).extend({
  attributes: z.unknown().nullable(),
  variants: z.array(drawerSystemVariantListItem),
});
export type DrawerSystemDetails = z.infer<typeof drawerSystemDetails>;

export const listDrawerSystemsQuery = paginationQuery.extend({
  search: optionalTrim(120),
  manufacturer: optionalTrim(120),
  active: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional(),
  includeDeleted: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional()
    .default(false),
});
export type ListDrawerSystemsQuery = z.infer<typeof listDrawerSystemsQuery>;

export const listDrawerSystemsResponse = paginatedResponse(drawerSystemListItem);
export type ListDrawerSystemsResponse = z.infer<typeof listDrawerSystemsResponse>;

export const listDrawerSystemVariantsQuery = paginationQuery.extend({
  search: optionalTrim(160),
  active: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional(),
  includeDeleted: z
    .union([z.boolean(), z.literal('true'), z.literal('false')])
    .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
    .optional()
    .default(false),
  // Drawer facets. Every one is optional and multi-valued: values within a
  // field are OR-ed, fields are AND-ed. Omitting a field means "any", so a
  // selection can never be narrowed into an empty result by a field the
  // catalog has no data for.
  modelSertar: optionalList(80),
  tipSertar: optionalList(80),
  descriere: optionalList(80),
  detaliu: optionalList(80),
  hBox: optionalList(40),
  color: optionalList(80),
  depthMm: optionalIntList(),
  /**
   * Ranking hint, never a filter: the depth the drawer actually needs. Results
   * are ordered by how close they sit to it, so an off-catalog depth like
   * 480mm surfaces the 500mm row instead of returning nothing.
   */
  nearDepthMm: z.coerce.number().int().positive().optional(),
});
export type ListDrawerSystemVariantsQuery = z.infer<typeof listDrawerSystemVariantsQuery>;

export const listDrawerSystemVariantsResponse = paginatedResponse(drawerSystemVariantListItem);
export type ListDrawerSystemVariantsResponse = z.infer<typeof listDrawerSystemVariantsResponse>;

// ---------- Faceted drawer selection ----------

/**
 * One selectable value in a drawer facet, with the number of catalog variants
 * behind it. Options are computed from the catalog, so an option that exists
 * here always has at least one product behind it.
 */
export const drawerVariantFacetOption = z.object({
  value: z.string(),
  label: z.string(),
  count: z.number().int().nonnegative(),
});
export type DrawerVariantFacetOption = z.infer<typeof drawerVariantFacetOption>;

/**
 * Facet lists for the drawer picker. Each field's options are computed with
 * every *other* field's selection applied but not its own, so a multi-select
 * never collapses to the single value already chosen.
 *
 * A field with no values in the catalog comes back empty and the UI hides it —
 * that is how `detaliu` disappears for Box drawers and reappears the day such
 * products are imported.
 */
export const drawerVariantFacets = z.object({
  modelSertar: z.array(drawerVariantFacetOption),
  tipSertar: z.array(drawerVariantFacetOption),
  descriere: z.array(drawerVariantFacetOption),
  hBox: z.array(drawerVariantFacetOption),
  detaliu: z.array(drawerVariantFacetOption),
  color: z.array(drawerVariantFacetOption),
  depthMm: z.array(drawerVariantFacetOption),
  /** Variants matching the current selection across all fields. */
  total: z.number().int().nonnegative(),
});
export type DrawerVariantFacets = z.infer<typeof drawerVariantFacets>;

export const drawerVariantFacetsQuery = listDrawerSystemVariantsQuery.omit({
  page: true,
  pageSize: true,
});
export type DrawerVariantFacetsQuery = z.infer<typeof drawerVariantFacetsQuery>;

export const createDrawerSystemRequest = z.object({
  name: z.string().trim().min(1).max(200),
  manufacturer: optionalTrim(120),
  active: z.boolean().default(true),
  attributes: z.unknown().optional(),
});
export type CreateDrawerSystemRequest = z.infer<typeof createDrawerSystemRequest>;

export const updateDrawerSystemRequest = createDrawerSystemRequest.partial();
export type UpdateDrawerSystemRequest = z.infer<typeof updateDrawerSystemRequest>;
